// OWNER: workspace module (FR-1.7): comments with @mentions and notifications on any record.
// Props are stable — other modules render <Comments entityType entityId />.
import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useLocation } from 'react-router';
import { MessageSquare, Pencil, Send, Trash2 } from 'lucide-react';
import {
  COMMENT_MAX_LENGTH,
  ROLE_LABELS,
  mentionToken,
  splitMentions,
  type CommentDto,
  type EntityType,
  type MentionableMember,
} from '@seventide/types';
import { useCommentMutations, useComments, useMentionable } from '../../features/workspace/api';
import { useCan, useAccessBlock } from '../../hooks/useCan';
import { useOptionalWorkspace } from '../../hooks/useWorkspace';
import { errorMessage } from '../../lib/api';
import { cn } from '../../lib/cn';
import { formatDateTime, formatRelative } from '../../lib/format';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/Dialog';
import { Skeleton } from '../ui/Feedback';
import { toast } from '../ui/toastStore';

export interface CommentsProps {
  entityType: EntityType;
  entityId: string;
  /** Optional heading override (default "Comments"). */
  title?: string;
  headingLevel?: 2 | 3;
}

/** Comments on a record, with @mentions (FR-1.7). Hidden records' comments are never returned by the API. */
export function Comments(props: CommentsProps) {
  const ws = useOptionalWorkspace();
  if (!ws || !props.entityId) return null;
  return <CommentsInner {...props} />;
}

function CommentsInner({ entityType, entityId, title = 'Comments', headingLevel = 2 }: CommentsProps) {
  const H = `h${headingLevel}` as 'h2' | 'h3';
  const headingId = useId();
  const list = useComments(entityType, entityId);
  const canComment = useCan('comments', 'edit');
  const block = useAccessBlock();
  const count = list.data?.total ?? 0;

  return (
    <section
      aria-labelledby={headingId}
      data-entity-type={entityType}
      data-entity-id={entityId}
      className="rounded-[var(--radius-card)] border border-border bg-surface p-4 shadow-card"
    >
      <H id={headingId} className="mb-3 flex items-center gap-2 text-lg">
        <MessageSquare aria-hidden="true" className="h-5 w-5 text-primary" />
        {title}
        {list.data && <span className="font-normal text-muted">({count})</span>}
      </H>

      {list.isPending ? (
        <div role="status" className="flex flex-col gap-3">
          <span className="sr-only">Loading comments</span>
          <Skeleton lines={3} />
        </div>
      ) : list.isError ? (
        <Alert
          tone="danger"
          title="We couldn't load the comments"
          action={
            <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
              Try again
            </Button>
          }
        >
          {errorMessage(list.error)}
        </Alert>
      ) : count === 0 ? (
        <p className="text-muted">
          {canComment
            ? 'No comments yet. Add a note or a question for the family below — type @ to mention someone.'
            : 'No comments yet.'}
        </p>
      ) : (
        <ol className="flex flex-col gap-3" aria-label={`${count} ${count === 1 ? 'comment' : 'comments'}`}>
          {list.data.items.map((c) => (
            <CommentItem key={c.id} comment={c} entityType={entityType} entityId={entityId} canEdit={canComment} />
          ))}
        </ol>
      )}

      {canComment ? (
        <div className="mt-4 border-t border-border pt-4">
          <CommentComposer entityType={entityType} entityId={entityId} />
        </div>
      ) : (
        block && (
          <p className="mt-4 text-muted">
            {block === 'read_only'
              ? 'New comments are paused while the workspace is read-only.'
              : 'This workspace is an archive, so new comments are turned off.'}
          </p>
        )
      )}
    </section>
  );
}

/** Renders a stored comment body: text with mention tokens shown as "@Name". */
export function CommentBody({ body }: { body: string }) {
  return (
    <p className="break-words whitespace-pre-wrap">
      {splitMentions(body).map((s, i) =>
        s.kind === 'text' ? (
          <span key={i}>{s.text}</span>
        ) : (
          <span key={i} className="rounded bg-primary-soft px-1 font-bold text-primary">
            @{s.name}
          </span>
        ),
      )}
    </p>
  );
}

function CommentItem({
  comment,
  entityType,
  entityId,
  canEdit,
}: {
  comment: CommentDto;
  entityType: EntityType;
  entityId: string;
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const { remove } = useCommentMutations(entityType, entityId);
  const author = comment.author?.name ?? (comment.author ? 'A member' : 'A former member');
  return (
    <li className="rounded-lg border border-border bg-surface-muted p-3">
      <div className="mb-1 flex flex-wrap items-baseline gap-x-2">
        <span className="font-bold">{comment.mine ? `${author} (you)` : author}</span>
        <time dateTime={comment.createdAt} title={formatDateTime(comment.createdAt)} className="text-sm text-muted">
          {formatRelative(comment.createdAt)}
        </time>
        {comment.edited && <span className="text-sm text-muted">(edited)</span>}
      </div>
      {editing ? (
        <CommentComposer
          entityType={entityType}
          entityId={entityId}
          existing={comment}
          onDone={() => setEditing(false)}
        />
      ) : (
        <CommentBody body={comment.body} />
      )}
      {comment.mine && canEdit && !editing && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" leftIcon={<Pencil aria-hidden="true" className="h-4 w-4" />} onClick={() => setEditing(true)}>
            Edit{' '}<span className="sr-only">your comment</span>
          </Button>
          <ConfirmDialog
            title="Delete this comment?"
            description="It will be removed for everyone. This cannot be undone."
            confirmLabel="Delete comment"
            tone="danger"
            onConfirm={async () => {
              await remove.mutateAsync(comment.id);
              toast({ title: 'Comment deleted', tone: 'success' });
            }}
            trigger={
              <Button variant="ghost" size="sm" leftIcon={<Trash2 aria-hidden="true" className="h-4 w-4" />}>
                Delete{' '}<span className="sr-only">your comment</span>
              </Button>
            }
          />
        </div>
      )}
    </li>
  );
}

interface Picked {
  name: string;
  userId: string;
}

/** Converts the composer's plain "@Name" text back to stored tokens for the people actually picked. */
export function encodeMentions(text: string, picked: readonly Picked[]): string {
  let out = text;
  // Longest names first so "@Ann Marie" wins over "@Ann".
  for (const p of [...picked].sort((a, b) => b.name.length - a.name.length)) {
    const plain = `@${p.name}`;
    let idx = out.indexOf(plain);
    while (idx !== -1) {
      const after = out[idx + plain.length];
      const before = idx > 0 ? out[idx - 1] : ' ';
      const boundaryOk = (!after || !/[\p{L}\p{N}]/u.test(after)) && !/\]/.test(before ?? '');
      if (boundaryOk) {
        const token = mentionToken(p.name, p.userId);
        out = out.slice(0, idx) + token + out.slice(idx + plain.length);
        idx = out.indexOf(plain, idx + token.length);
      } else idx = out.indexOf(plain, idx + plain.length);
    }
  }
  return out;
}

/** Stored body → composer text + the people already mentioned. */
export function decodeMentions(body: string): { text: string; picked: Picked[] } {
  const picked: Picked[] = [];
  const text = splitMentions(body)
    .map((s) => {
      if (s.kind === 'text') return s.text;
      if (!picked.some((p) => p.userId === s.userId)) picked.push({ name: s.name, userId: s.userId });
      return `@${s.name}`;
    })
    .join('');
  return { text, picked };
}

/** The "@query" being typed just before the caret, if any. */
export function activeMentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at === -1) return null;
  if (at > 0 && /[\p{L}\p{N}]/u.test(upto[at - 1] ?? '')) return null; // part of an email address
  const query = upto.slice(at + 1);
  if (query.length > 40 || /[\n@]/.test(query) || /\s{2}/.test(query)) return null;
  return { start: at, query };
}

function CommentComposer({
  entityType,
  entityId,
  existing,
  onDone,
}: {
  entityType: EntityType;
  entityId: string;
  existing?: CommentDto;
  onDone?: () => void;
}) {
  const initial = useMemo(() => (existing ? decodeMentions(existing.body) : { text: '', picked: [] }), [existing]);
  const [text, setText] = useState(initial.text);
  const [picked, setPicked] = useState<Picked[]>(initial.picked);
  const [error, setError] = useState<string | null>(null);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLTextAreaElement>(null);
  const ids = useId().replace(/:/g, '');
  const labelId = `cl${ids}`;
  const hintId = `ch${ids}`;
  const errorId = `ce${ids}`;
  const listId = `cm${ids}`;
  const location = useLocation();
  const { create, update } = useCommentMutations(entityType, entityId, `${location.pathname}${location.search}`);
  const members = useMentionable(entityType, entityId, true);
  const busy = create.isPending || update.isPending;

  const options: MentionableMember[] = useMemo(() => {
    if (!mention || !members.data) return [];
    const q = mention.query.trim().toLowerCase();
    return members.data.items.filter((m) => !q || m.name.toLowerCase().split(/\s+/).some((w) => w.startsWith(q)) || m.name.toLowerCase().startsWith(q)).slice(0, 6);
  }, [mention, members.data]);
  const open = Boolean(mention) && options.length > 0;

  const syncMention = (value: string, caret: number) => {
    const m = activeMentionQuery(value, caret);
    setMention(m);
    setActive(0);
  };

  const choose = (m: MentionableMember) => {
    if (!mention) return;
    const before = text.slice(0, mention.start);
    const after = text.slice(mention.start + 1 + mention.query.length);
    const insert = `@${m.name} `;
    const next = before + insert + after.replace(/^\s+/, '');
    setText(next);
    setPicked((p) => (p.some((x) => x.userId === m.userId) ? p : [...p, { name: m.name, userId: m.userId }]));
    setMention(null);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      const pos = before.length + insert.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((a) => (a + 1) % options.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => (a - 1 + options.length) % options.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const o = options[active];
        if (o) {
          e.preventDefault();
          choose(o);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMention(null);
        return;
      }
    }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void submit();
    }
  };

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      setError('Write a comment before sending it.');
      ref.current?.focus();
      return;
    }
    if (trimmed.length > COMMENT_MAX_LENGTH) {
      setError('Keep comments under 4,000 characters.');
      return;
    }
    setError(null);
    const body = encodeMentions(trimmed, picked.filter((p) => trimmed.includes(`@${p.name}`)));
    try {
      if (existing) {
        await update.mutateAsync({ id: existing.id, body });
        toast({ title: 'Comment updated', tone: 'success' });
        onDone?.();
      } else {
        const res = await create.mutateAsync(body);
        setText('');
        setPicked([]);
        toast({
          title: 'Comment added',
          description: res.mentions.length
            ? `${res.mentions.map((m) => m.name ?? 'A member').join(' and ')} will get a notification.`
            : undefined,
          tone: 'success',
        });
      }
    } catch (err) {
      setError(errorMessage(err, "We couldn't save the comment. Please try again."));
    }
  };

  const remaining = COMMENT_MAX_LENGTH - text.length;
  return (
    <div className="flex flex-col gap-2">
      <label id={labelId} htmlFor={`ta${ids}`} className="font-bold">
        {existing ? 'Edit your comment' : 'Add a comment'}
      </label>
      <p id={hintId} className="text-muted">
        Type @ to mention someone. They will get a notification. Only people who can see this record can be mentioned.
      </p>
      {error && (
        <p id={errorId} className="font-bold text-danger" role="alert">
          <span className="sr-only">Error: </span>
          {error}
        </p>
      )}
      <div className="relative">
        <textarea
          id={`ta${ids}`}
          ref={ref}
          rows={3}
          value={text}
          maxLength={COMMENT_MAX_LENGTH + 200}
          aria-autocomplete="list"
          aria-controls={open ? listId : undefined}
          aria-activedescendant={open ? `${listId}-${active}` : undefined}
          aria-describedby={cn(hintId, error && errorId) || undefined}
          aria-invalid={error ? true : undefined}
          onChange={(e) => {
            setText(e.target.value);
            syncMention(e.target.value, e.target.selectionStart ?? e.target.value.length);
          }}
          onKeyDown={onKeyDown}
          onClick={(e) => syncMention(text, e.currentTarget.selectionStart ?? text.length)}
          onBlur={() => window.setTimeout(() => setMention(null), 150)}
          className={cn(
            'min-h-24 w-full rounded-lg border-2 border-border-strong bg-surface px-3 py-2 leading-relaxed text-text',
            error && 'border-danger',
          )}
        />
        {open && (
          <ul
            id={listId}
            role="listbox"
            aria-label="People you can mention"
            className="absolute right-0 left-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-lg border-2 border-border-strong bg-surface shadow-card"
          >
            {options.map((o, i) => (
              <li
                key={o.userId}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(o);
                }}
                className={cn(
                  'flex min-h-11 cursor-pointer items-center justify-between gap-2 px-3 py-2',
                  i === active && 'bg-primary-soft',
                )}
              >
                <span className="font-bold">{o.name}</span>
                <span className="text-sm text-muted">{ROLE_LABELS[o.role]}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="sr-only" aria-live="polite">
        {open
          ? `${options.length} ${options.length === 1 ? 'person matches' : 'people match'}. Use the up and down arrows, then Enter to choose.`
          : ''}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={cn('text-sm', remaining < 0 ? 'font-bold text-danger' : 'text-muted')} aria-live="polite">
          {remaining < 200 ? `${remaining} characters left` : ''}
        </span>
        <div className="flex flex-wrap gap-2">
          {existing && (
            <Button variant="secondary" onClick={onDone} disabled={busy}>
              Cancel
            </Button>
          )}
          <Button onClick={() => void submit()} loading={busy} leftIcon={<Send aria-hidden="true" className="h-5 w-5" />}>
            {existing ? 'Save changes' : 'Post comment'}
          </Button>
        </div>
      </div>
    </div>
  );
}
